import type { FC, ReactNode } from "react";
import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { GridColDef, GridFilterInputValueProps } from "@mui/x-data-grid-premium";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { DynamicGrid } from "../dynamicGrid";
import type { GridViewRegistration, RegistrationStatus } from "../api/registrations.types";
import { RegistrationStatusChip } from "./RegistrationStatus";
import { TriangleAlertIcon } from "./icons";
import { StatusSquare } from "./RegistrationStatusDot";

type StatusNote = { kind: "awaitingClaim" | "ownCopy"; title: string; label?: ReactNode };

export type StatusColumnNotes = {
  width: number;
  noteFor: (row: GridViewRegistration) => StatusNote | null | undefined;
};

const FILTER_STATUSES: ReadonlyArray<RegistrationStatus["status"]> = ["draft", "review", "done", "completion", "audit"];

type StatusColumnFilterInputProps = GridFilterInputValueProps & {
  statuses: ReadonlyArray<RegistrationStatus["status"]>;
  noteFor?: StatusColumnNotes["noteFor"];
};

export const statusColumn = (
  t: (translation: TranslationKey) => string,
  notes?: StatusColumnNotes,
  filterStatuses = FILTER_STATUSES
): GridColDef<GridViewRegistration> => {
  return {
    ...DynamicGrid.requiredColDef({ headerName: t("RegistrationGrid.StatusHeader"), field: "status" }),
    width: notes?.width ?? 180,
    filterOperators: [
      {
        value: "is",
        getApplyFilterFn: filterItem => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) return null;
          return value => value === filterItem.value;
        },
        InputComponent: StatusColumnFilterInput,
        InputComponentProps: { statuses: filterStatuses, noteFor: notes?.noteFor },
      },
    ],
    /** The value getter value is used for sorting and searching */
    valueGetter: (_, row) => {
      const note = notes?.noteFor(row);
      return note === undefined ? t(`RegistrationStatus.${row.status.status}`) : (note?.title ?? "");
    },
    renderCell: ({ row }) => {
      const note = notes?.noteFor(row);
      if (note)
        return (
          <DynamicGrid.Cell>
            <StatusNoteCell note={note} />
          </DynamicGrid.Cell>
        );
      if (note === null) return <DynamicGrid.EmptyCell />;
      return (
        <DynamicGrid.Cell>
          <RegistrationStatusChip status={row.status.status} />
        </DynamicGrid.Cell>
      );
    },
  };
};

const StatusNoteCell: FC<{ note: StatusNote }> = ({ note }) => (
  <Typography
    component="div"
    sx={{
      minWidth: 0,
      fontSize: theme => theme.typography.pxToRem(12),
      lineHeight: 1.3,
      fontWeight: 400,
    }}
  >
    <Box
      component="span"
      sx={{ display: "inline-block", mr: 1, verticalAlign: note.kind === "ownCopy" ? "-3px" : "0px" }}
    >
      {note.kind === "ownCopy" ? (
        <TriangleAlertIcon size={14} color={theme => theme.palette.warning.main} />
      ) : (
        <StatusSquare sx={theme => ({ bgcolor: theme.palette.brandOrange["500"] })} />
      )}
    </Box>
    {note.label ?? note.title}
  </Typography>
);

const StatusColumnFilterInput = ({
  item,
  applyValue,
  focusElementRef,
  apiRef,
  statuses,
  noteFor,
}: StatusColumnFilterInputProps) => {
  const { t } = useTranslation();
  const noteTitles = noteFor
    ? [
        ...new Set(
          [...apiRef.current.getRowModels().values()].flatMap(row => noteFor(row as GridViewRegistration)?.title ?? [])
        ),
      ]
    : [];

  return (
    <Select
      fullWidth
      size="small"
      value={item.value ?? ""}
      onChange={event => applyValue({ ...item, value: event.target.value })}
      inputRef={focusElementRef}
    >
      <MenuItem hidden sx={{ display: "none" }} value="">
        {t("Filter.DefaultValue")}
      </MenuItem>
      {[...statuses.map(status => t(`RegistrationStatus.${status}`)), ...noteTitles].map(label => (
        <MenuItem key={label} value={label}>
          {label}
        </MenuItem>
      ))}
    </Select>
  );
};
